import type {
	AvailabilityStatusCode$options,
	ConnectorType$options,
	FUEL_TYPE$options,
	GetGetYourGuideTours$result,
	GetPoiFilters$result,
	ImageFragment$data,
	OptionId$options,
	POI_TYPE_QUERY$options,
	PoiFilterId$options,
	POIFragment$data,
	REVIEW_MENTION$options,
	SUB_TYPE$options,
} from '$houdini';
import type { IconName } from '$lib/components/icons/types';
import type { Price, Scheme } from '$lib/components/types';
import type { RecursiveOmit } from '$lib/utils/types';
import type { PriceCategory } from '../map/markers/markerContent/types';

/** PartnerPoiCategory contains the name of all the partner categories */
const partnerPoiCategoryList = ['PARTNER', 'PARTNER2'] as const;

export type PartnerPoiCategory = (typeof partnerPoiCategoryList)[number];

/** PoiCategory contains the name of all the categories that can be queried to the bff */
export const poiCategoryList = [
	'ACCOMMODATION',
	'RESTAURANT',
	'TOURISM',
	'SERVICE_GAS_STATION',
	'EV_SERVICE',
	'PARKING',
	'REST_STOP',
	...partnerPoiCategoryList,
] satisfies POI_TYPE_QUERY$options[];
export type PoiCategory = (typeof poiCategoryList)[number];

/** MainPoiCategory is a subtype of PoiCategory that contains the name of all the categories used in the main page */
export const mainPoiCategoryList = [
	'ACCOMMODATION',
	'RESTAURANT',
	'TOURISM',
	'SERVICE_GAS_STATION',
	'EV_SERVICE',
	'PARKING',
	...partnerPoiCategoryList,
] satisfies PoiCategory[];
export type MainPoiCategory = (typeof mainPoiCategoryList)[number];

export const SEOPoiCategories = [
	'ACCOMMODATION',
	'RESTAURANT',
	'TOURISM',
	'SERVICE_GAS_STATION',
	'EV_SERVICE',
	'PARKING',
] satisfies PoiCategory[];

/** Poi Category that are referenced from SEO */
export type SEOPoiCategory = (typeof SEOPoiCategories)[number];

/** PoiData is the type of the data returned by the bff for a poi */
export type PoiData = RecursiveOmit<POIFragment$data, ' $fragments'>;
export type AccommodationPoiData = Extract<PoiData, { __typename: 'AccommodationPOI' }>;
export type AccommodationMichelinPoiData = Extract<
	PoiData,
	{ __typename: 'AccommodationMichelinPOI' }
>;
export type ParkingPoiData = Extract<PoiData, { __typename: 'ParkingPOI' }>;
export type GasStationPoiData = Extract<PoiData, { __typename: 'ServiceGasStationPOI' }>;
export type RestaurantPoiData = Extract<PoiData, { __typename: 'RestaurantPOI' }>;
export type RestaurantMichelinPoiData = Extract<PoiData, { __typename: 'RestaurantMichelinPOI' }>;
export type TourismPoiData = Extract<PoiData, { __typename: 'TourismPOI' }>;
export type ServiceEVPoiData = Extract<PoiData, { __typename: 'ServiceEVPOI' }>;
export type ServicePoiData = Extract<PoiData, { __typename: 'ServicePOI' }>;
export type RestStopPoiData = Extract<PoiData, { __typename: 'ServiceRestStopPOI' }>;
export type Partner1PoiData = Extract<PoiData, { __typename: 'PartnerPOI' }>;
export type Partner2PoiData = Extract<PoiData, { __typename: 'Partner2POI' }>;
/** MainPoiData is a subtype of PoiData that contains all the types of poi data that are displayed in the main page */
export type MainPoiData =
	| AccommodationPoiData
	| AccommodationMichelinPoiData
	| ParkingPoiData
	| GasStationPoiData
	| RestaurantPoiData
	| RestaurantMichelinPoiData
	| TourismPoiData
	| ServiceEVPoiData
	| ServicePoiData;
/** DatasheetPoiData is a subtype of PoiData that contains all the types of poi data that are displayed in a datasheet */
export type DatasheetPoiData =
	| AccommodationPoiData
	| AccommodationMichelinPoiData
	| RestaurantPoiData
	| RestaurantMichelinPoiData
	| TourismPoiData
	| ServiceEVPoiData
	| RestStopPoiData
	| Partner1PoiData
	| Partner2PoiData;
/** DatasheetSideBarPoiData is a subtype of PoiData that contains all the types of poi data that are displayed in a datasheet with a sidebar */
export type DatasheetSideBarPoiData =
	| AccommodationPoiData
	| TourismPoiData
	| RestaurantPoiData
	| RestaurantMichelinPoiData
	| ServiceEVPoiData;
/** SummaryPoiData is a subtype of PoiData that contains all the types of poi data that are displayed in a summary */
export type SummaryPoiData = ParkingPoiData | GasStationPoiData | ServicePoiData;

const partnerPoiTypeList = ['PartnerPOI', 'Partner2POI'] as const;

export type PartnerPoiType = (typeof partnerPoiTypeList)[number];

/** PoiType contains the name of all the types of poi that can be returned by the bff */
export const poiTypeList = [
	'AccommodationPOI',
	'AccommodationMichelinPOI',
	'RestaurantPOI',
	'RestaurantMichelinPOI',
	'TourismPOI',
	'ServiceGasStationPOI',
	'ServiceEVPOI',
	'ParkingPOI',
	'ServiceRestStopPOI',
	'ServicePOI',
	...partnerPoiTypeList,
] satisfies PoiData['__typename'][];
export type PoiType = (typeof poiTypeList)[number];

/** MainPoiType is a subtype of PoiType that contains the name of all the types of the poi that are displayed in the main page */
export const mainPoiTypeList = [
	'AccommodationPOI',
	'AccommodationMichelinPOI',
	'RestaurantPOI',
	'RestaurantMichelinPOI',
	'TourismPOI',
	'ServiceGasStationPOI',
	'ServiceEVPOI',
	'ParkingPOI',
	'ServicePOI',
] satisfies MainPoiData['__typename'][];
export type MainPoiType = (typeof mainPoiTypeList)[number];

/** DatasheetPoiType is a subtype of PoiType that contains the name of all the types of the poi that are displayed in a datasheet */
export const datasheetPoiTypeList = [
	'AccommodationPOI',
	'AccommodationMichelinPOI',
	'RestaurantPOI',
	'RestaurantMichelinPOI',
	'TourismPOI',
	'ServiceEVPOI',
	'ServiceRestStopPOI',
	...partnerPoiTypeList,
] satisfies DatasheetPoiData['__typename'][];
export type DatasheetPoiType = (typeof datasheetPoiTypeList)[number];

/** DatasheetSideBarPoiType is a subtype of PoiType that contains the name of all the types of the poi that are displayed in a datasheet with a sidebar */
export const datasheetSideBarPoiTypeList = [
	'AccommodationPOI',
	'TourismPOI',
	'RestaurantPOI',
	'RestaurantMichelinPOI',
	'ServiceEVPOI',
] satisfies DatasheetSideBarPoiData['__typename'][];
export type DatasheetSideBarPoiType = (typeof datasheetSideBarPoiTypeList)[number];

/** SummaryPoiType is a subtype of PoiType that contains the name of all the types of the poi that are displayed in a summary */
export const summaryPoiTypeList = [
	'ServiceGasStationPOI',
	'ParkingPOI',
	'ServicePOI',
] satisfies SummaryPoiData['__typename'][];
export type SummaryPoiType = (typeof summaryPoiTypeList)[number];

export type PoiScheme = Extract<
	Scheme,
	| 'hotel'
	| 'hotelMichelin'
	| 'restaurant'
	| 'restaurantMichelin'
	| 'tourism'
	| 'electric'
	| 'parking'
	| 'energy'
	| 'rest'
	| 'restSponsored'
>;
export type PoiRecommendationScheme = Extract<
	Scheme,
	| 'hotel'
	| 'hotelMichelin'
	| 'restaurant'
	| 'restaurantMichelin'
	| 'tourism'
	| 'electric'
	| 'parking'
	| 'energy'
	| 'rest'
>;
export type PoiSummaryScheme = Extract<
	Scheme,
	| 'hotel'
	| 'hotelMichelin'
	| 'restaurant'
	| 'restaurantMichelin'
	| 'tourism'
	| 'parking'
	| 'energy'
	| 'rest'
>;
export type PoiCardScheme = Extract<
	Scheme,
	| 'hotel'
	| 'hotelMichelin'
	| 'restaurant'
	| 'restaurantMichelin'
	| 'tourism'
	| 'parking'
	| 'energy'
	| 'electric'
	| 'rest'
	| 'partner'
	| 'partner2'
>;
export type DatasheetPoiScheme = Extract<
	Scheme,
	'hotel' | 'hotelMichelin' | 'restaurant' | 'restaurantMichelin' | 'tourism' | 'electric' | 'rest'
>;

export type PicturePoiCardScheme = Extract<
	Scheme,
	'hotel' | 'hotelMichelin' | 'restaurant' | 'restaurantMichelin' | 'tourism'
>;

export type AccommodationSubtype = SUB_TYPE$options;

export type FuelType =
	| 'SP_95'
	| 'SP_98'
	| 'E_10'
	| 'DIESEL'
	| 'PREMIUM_DIESEL'
	| 'GPL'
	| 'METHANE'
	| 'ETHANOL';

export interface FuelPrice {
	type: FUEL_TYPE$options;
	price: Price;
	filterOptionId: string;
	priceCategory: PriceCategory;
}
export type FuelPrices = FuelPrice[];
export interface Review {
	mention: REVIEW_MENTION$options;
	score: number;
	count: number | null;
}
export interface SplittedAddress {
	firstLine: string;
	secondLine: string;
}

export interface PriceRange {
	min: number;
	max: number | null;
	currency: string;
}

export interface MichelinDistinction {
	icon: IconName;
	amount: number;
	label: string;
}

export type EVPaymentMethodId = 'CREDIT_CARD' | 'QR_CODE' | 'SERVICE_PROVIDER' | 'CASH';
export interface EVPaymentMethod {
	id: EVPaymentMethodId;
	name: string;
}
export interface Spot {
	power: number;
	count: number;
}
export interface Connector {
	type: ConnectorType$options;
	name: string;
	count: number;
	spots: Spot[];
}

export interface DateRange {
	startDate: string;
	endDate: string;
}

export type PoiFilters = RecursiveOmit<
	NonNullable<GetPoiFilters$result['getPoiFilters']['filtersV2']>,
	' $fragments'
>;
export type PoiFilter = PoiFilters['PARKING'][number];
export type PoiFilterValue =
	| number
	| (number | undefined)[]
	| boolean
	| OptionId$options[]
	| undefined
	| DateRange;

export type PoiFilterId = PoiFilterId$options;
export type PoiFilterOptionId = OptionId$options;

export type PoiImages = Partial<RecursiveOmit<ImageFragment$data, ' $fragments'>>;

export interface AvailabilityStatus {
	code: AvailabilityStatusCode$options;
	availableAccommodationsCount: number | null;
	totalAccommodationsCount: number | null;
}

export type GetYourGuideTour = Extract<
	GetGetYourGuideTours$result['getGetYourGuideTours'],
	{ __typename: 'SearchGetYourGuideToursFound' }
>['tours'][number];
